/** Tab-local original-intent recovery. Draft acceptance is distinct from workspace writing. */
import { ApiError, createFeedbackDraft, saveFeedbackDraft, discardFeedbackDraft, isCurrentFeedbackDraftScope } from './api';
import { refreshFeedbackDraft } from './queryClient';
import type { FeedbackDraftCommand, FeedbackDraftScope, FeedbackDraftResult, FeedbackDraftDiscardResult } from './types';

type Outcome = FeedbackDraftResult | FeedbackDraftDiscardResult;
export type FeedbackDraftCommandState =
  | { status: 'ready' | 'sending' | 'unknown' | 'rejected' | 'unavailable' }
  | { status: 'retired'; outcome: 'unknown' }
  | { status: 'accepted'; result: Outcome };

function acknowledgement(value: Outcome, command: FeedbackDraftCommand): Outcome {
  if (command.action === 'discard') {
    if (!value || value.schema_version !== 'feedback-draft-discard-result.v1' || value.discarded !== true
      || Object.keys(value).sort().join(',') !== 'discarded,schema_version') throw new Error('invalid_draft_acknowledgement');
    return { ...value };
  }
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!value || value.schema_version !== 'feedback-draft-result.v1' || !uuid.test(value.feedback_id)
    || !uuid.test(value.revision_id) || !Number.isInteger(value.version) || value.version < 1 || value.version > 2147483647
    || (command.action === 'create' ? value.version !== 1
      : value.feedback_id.toLowerCase() !== command.feedbackId.toLowerCase()
        || (value.version !== command.body.expected_version && value.version !== command.body.expected_version + 1))
    || Object.keys(value).sort().join(',') !== 'feedback_id,revision_id,schema_version,version') {
    throw new Error('invalid_draft_acknowledgement');
  }
  return { ...value };
}

function errorCode(error: unknown): unknown {
  return error instanceof ApiError && error.detail && typeof error.detail === 'object' && 'code' in error.detail
    ? error.detail.code : null;
}

export function captureFeedbackDraftCommand(scope: FeedbackDraftScope, command: FeedbackDraftCommand,
  recovery: { restoredUnknown?: boolean } = {}) {
  const capturedScope = { ...scope };
  const serialized = JSON.stringify(command);
  const intent = (): FeedbackDraftCommand => JSON.parse(serialized) as FeedbackDraftCommand;
  const current = () => isCurrentFeedbackDraftScope(capturedScope);
  let state: FeedbackDraftCommandState = { status: recovery.restoredUnknown ? 'unknown' : 'ready' };
  let unresolved = recovery.restoredUnknown ?? false;
  let inFlight: Promise<Outcome> | null = null;

  async function run(signal?: AbortSignal): Promise<Outcome> {
    state = { status: 'sending' };
    const original = intent();
    try {
      const options = { scope: capturedScope, signal };
      const value = original.action === 'create' ? await createFeedbackDraft(original.body, options)
        : original.action === 'save' ? await saveFeedbackDraft(original.feedbackId, original.body, options)
          : await discardFeedbackDraft(original.feedbackId, original.body, options);
      const result = acknowledgement(value, original);
      unresolved = false;
      state = { status: 'accepted', result };
      const feedbackId = result.schema_version === 'feedback-draft-result.v1' ? result.feedback_id
        : original.action === 'discard' ? original.feedbackId : null;
      if (feedbackId) void Promise.resolve().then(() => {
        if (current()) return refreshFeedbackDraft(capturedScope, feedbackId, {
          discarded: original.action === 'discard',
          capturedWorkspaceId: original.action === 'discard' ? undefined : original.body.workspace?.workspace_id,
        });
      }).catch(() => {});
      return { ...result };
    } catch (error) {
      const code = errorCode(error);
      if (code === 'feedback_operation_unresolved' || code === 'feedback_operation_retired'
        || !(error instanceof ApiError && error.status >= 400 && error.status < 500)) unresolved = true;
      // Discard retires its key without a receipt; erasure can do the same.
      // Neither retirement nor a subsequent GET404 proves an unknown discard.
      state = code === 'feedback_operation_retired' ? { status: 'retired', outcome: 'unknown' }
        : { status: unresolved ? 'unknown' : 'rejected' };
      throw error;
    } finally { inFlight = null; }
  }

  return {
    // A shared continuation may adopt a command dispatched by another controller.
    // Its later 4xx cannot prove that the earlier attempt was never accepted.
    adoptUnresolved(): void {
      if (!current() || state.status === 'accepted') return;
      unresolved = true;
      state = { status: 'unknown' };
    },
    getState(): FeedbackDraftCommandState {
      if (!current()) return { status: 'unavailable' };
      return state.status === 'accepted' ? { ...state, result: { ...state.result } } : { ...state };
    },
    getIntent(): FeedbackDraftCommand | null { return current() ? intent() : null; },
    send(signal?: AbortSignal): Promise<Outcome> {
      if (!current() || signal?.aborted) return Promise.reject(new DOMException('Draft context changed', 'AbortError'));
      if (inFlight) return inFlight;
      if (state.status === 'accepted') return Promise.resolve({ ...state.result });
      inFlight = run(signal);
      return inFlight;
    },
  };
}
